import React from 'react'
import { ViewportPortal, useStore } from '@xyflow/react'
import type { MovingRect } from './utils/canvasAlignment'
import { UNION_CORNER_RADIUS } from '@/src/renderer/components/layout/ContentUnionShape/unionPath'
import './AdoptionMarks.scss'

/** Eine Marke: das Rechteck (Canvas-Koordinaten) und der Ton des Bereichs, um den es geht. */
export interface AdoptionMark {
  id: string
  rect: MovingRect
  accent: string
}

export interface AdoptionMarksProps {
  /** Der Bereich, der den getragenen Zettel bekäme — oder `null`. */
  area: AdoptionMark | null
  /** Die Zettel, die ein getragener Bereich bekäme. */
  notes: AdoptionMark[]
}

/** Bildschirm-Breite der Kontur; 1,5px wie der Umriss beim Platz tauschen. */
const STROKE = 1.5
/** Radius eines Zettels (siehe `NoteNode.scss`). */
const NOTE_RADIUS = 2

/**
 * Der **Aufnahme-Umriss** — zeigt, während man zieht, wohin ein Zettel gehören
 * wird (die Kontur um den Bereich) oder welche Zettel ein Bereich bekommen wird
 * (die Konturen um sie). Reine Rückmeldung, ohne Zeiger; die Entscheidung
 * fällt beim Loslassen (`settleAreas` in `CanvasFlow`).
 */
export const AdoptionMarks: React.FC<AdoptionMarksProps> = ({ area, notes }) => {
  const zoom = useStore(state => state.transform[2]) || 1
  if (!area && notes.length === 0) return null
  const width = STROKE / zoom
  const mark = (entry: AdoptionMark, kind: 'area' | 'note') => (
    <div
      key={`${kind}:${entry.id}`}
      className={`canvas-adopt-mark canvas-adopt-mark--${kind}`}
      data-adopt-mark={entry.id}
      style={{
        left: entry.rect.x,
        top: entry.rect.y,
        width: entry.rect.width,
        height: entry.rect.height,
        '--adopt-edge': entry.accent,
        '--adopt-width': `${width}px`,
        '--adopt-radius': `${kind === 'area' ? UNION_CORNER_RADIUS : NOTE_RADIUS}px`,
      } as React.CSSProperties}
    />
  )
  return (
    <ViewportPortal>
      {area && mark(area, 'area')}
      {notes.map(entry => mark(entry, 'note'))}
    </ViewportPortal>
  )
}
